/**
 * Hook to manage Apple Pay payment sessions
 *
 * @see https://developer.apple.com/documentation/applepayontheweb/apple-pay-js-api
 */
import {useCallback, useRef, useState} from 'react';

import {logger, serializeError} from '../../../../../utils/logger';

import type {
  ApplePayMerchantSession,
  ApplePayPayment,
  ApplePayPaymentRequest,
  ApplePaySessionState,
} from './types';

/**
 * Apple Pay JS API version.
 * Version 14 supports iOS 18+ third-party browser QR code flow.
 * @see https://developer.apple.com/documentation/apple_pay_on_the_web/apple_pay_js_api
 */
const APPLE_PAY_VERSION = 14;

interface UseApplePaySessionOptions {
  /** Handler for merchant validation (must call server) */
  onMerchantValidation: (validationURL: string) => Promise<ApplePayMerchantSession>;
  /** Handler for payment authorization */
  onPaymentAuthorized: (payment: ApplePayPayment) => Promise<boolean>;
  /** Called when payment is cancelled */
  onCancel?: () => void;
  /** Called on error */
  onError?: (error: Error) => void;
}

interface UseApplePaySessionResult {
  /** Current session state */
  state: ApplePaySessionState;
  /** Start a new payment session with backend-provided payment request */
  startSession: (paymentRequest: ApplePayPaymentRequest) => void;
  /** Abort the current session */
  abortSession: () => void;
}

/**
 * Manage Apple Pay payment sessions.
 *
 * Flow:
 * 1. Fetch payment_request from backend (Phase 2)
 * 2. User clicks Apple Pay button -> startSession(paymentRequest)
 * 3. ApplePaySession created with backend-provided config
 * 4. onvalidatemerchant fires -> call server for merchant session (Phase 4)
 * 5. Payment sheet shown to user
 * 6. User authenticates (Face ID/Touch ID/passcode)
 * 7. onpaymentauthorized fires -> process payment with token (Phase 5)
 * 8. completePayment() called with success/failure status (Phase 7)
 *
 * @example
 * ```tsx
 * const {state, startSession} = useApplePaySession({
 *   onMerchantValidation: (url) => applePayService.validateMerchant(url),
 *   onPaymentAuthorized: async (payment) => {
 *     const result = await processPayment(payment.token);
 *     return result.success;
 *   },
 * });
 *
 * // When user clicks Apple Pay button:
 * const paymentRequest = await applePayService.getPaymentRequest(serviceId);
 * startSession(paymentRequest);
 * ```
 */
export function useApplePaySession(options: UseApplePaySessionOptions): UseApplePaySessionResult {
  const {onMerchantValidation, onPaymentAuthorized, onCancel, onError} = options;

  const [state, setState] = useState<ApplePaySessionState>({
    isActive: false,
    isValidating: false,
    isAuthorizing: false,
    error: null,
  });

  const sessionRef = useRef<ApplePaySession | null>(null);

  const handleError = useCallback(
    (error: Error) => {
      logger.error('useApplePaySession', 'Payment error', serializeError(error));
      setState(prev => ({...prev, error: error.message, isActive: false}));
      onError?.(error);
    },
    [onError],
  );

  const abortSession = useCallback(() => {
    if (sessionRef.current) {
      try {
        sessionRef.current.abort();
      } catch {
        // Session might already be completed
      }
      sessionRef.current = null;
    }
    setState({
      isActive: false,
      isValidating: false,
      isAuthorizing: false,
      error: null,
    });
  }, []);

  const startSession = useCallback(
    (paymentRequest: ApplePayPaymentRequest) => {
      // Validate browser support
      if (typeof window === 'undefined' || !window.ApplePaySession) {
        handleError(new Error('Apple Pay is not supported in this browser'));
        return;
      }

      // Abort any existing session
      if (sessionRef.current) {
        abortSession();
      }

      try {
        logger.info('useApplePaySession', 'Creating payment session', {
          amount: paymentRequest.total.amount,
          merchantLabel: paymentRequest.total.label,
        });

        // Create new session
        const session = new ApplePaySession(APPLE_PAY_VERSION, paymentRequest);
        sessionRef.current = session;

        setState({
          isActive: true,
          isValidating: false,
          isAuthorizing: false,
          error: null,
        });

        // Handle merchant validation
        // CRITICAL: This must call your server, which then calls Apple's servers
        session.onvalidatemerchant = async event => {
          logger.info('useApplePaySession', 'Merchant validation started', {
            validationURL: event.validationURL,
          });

          setState(prev => ({...prev, isValidating: true}));

          try {
            const merchantSession = await onMerchantValidation(event.validationURL);

            if (!merchantSession) {
              throw new Error('Failed to get merchant session');
            }

            session.completeMerchantValidation(merchantSession);
            setState(prev => ({...prev, isValidating: false}));

            logger.info('useApplePaySession', 'Merchant validation complete');
          } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            handleError(new Error(`Merchant validation failed: ${error.message}`));
            abortSession();
          }
        };

        // Handle payment authorization
        session.onpaymentauthorized = async event => {
          logger.info('useApplePaySession', 'Payment authorized, processing', {
            network: event.payment.token.paymentMethod.network,
            displayName: event.payment.token.paymentMethod.displayName,
          });

          setState(prev => ({...prev, isAuthorizing: true}));

          try {
            const success = await onPaymentAuthorized(event.payment);

            session.completePayment({
              status: success ? ApplePaySession.STATUS_SUCCESS : ApplePaySession.STATUS_FAILURE,
            });

            setState({
              isActive: false,
              isValidating: false,
              isAuthorizing: false,
              error: success ? null : 'Payment failed',
            });

            if (success) {
              logger.info('useApplePaySession', 'Payment completed successfully');
            } else {
              logger.warn('useApplePaySession', 'Payment processing failed');
            }
          } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));

            session.completePayment({
              status: ApplePaySession.STATUS_FAILURE,
            });

            handleError(new Error(`Payment processing failed: ${error.message}`));
          } finally {
            sessionRef.current = null;
          }
        };

        // Handle cancellation
        session.oncancel = () => {
          logger.info('useApplePaySession', 'Payment cancelled by user');

          setState({
            isActive: false,
            isValidating: false,
            isAuthorizing: false,
            error: null,
          });

          sessionRef.current = null;
          onCancel?.();
        };

        // Start the session - this shows the payment sheet
        session.begin();
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        handleError(new Error(`Failed to start Apple Pay session: ${error.message}`));
      }
    },
    [onMerchantValidation, onPaymentAuthorized, onCancel, handleError, abortSession],
  );

  return {
    state,
    startSession,
    abortSession,
  };
}

export default useApplePaySession;
